<template>
  <div class="contact-view">
    <!-- ページヘッダー -->
    <section class="page-hero">
      <div class="container">
        <h1 class="page-hero__title">Contact</h1>
        <p class="page-hero__subtitle">お問い合わせ</p>
      </div>
    </section>

    <!-- お問い合わせフォームセクション -->
    <base-section
      title="お問い合わせフォーム"
      subtitle="ご質問・ご相談はお気軽にお問い合わせください"
    >
      <div class="contact-form-wrapper">
        <form class="contact-form" @submit.prevent>
          <div class="form-group">
            <label class="form-label">お名前 <span class="required">※必須</span></label>
            <input 
              class="form-control" 
              v-model="name" 
              placeholder="例）田中 太郎"
              :class="{ 'has-error': errors.name }"
            >
            <p v-if="errors.name" class="error-message">{{ errors.name }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">フリガナ <span class="required">※必須</span></label>
            <input 
              class="form-control" 
              v-model="kana" 
              placeholder="例）タナカ タロウ"
              :class="{ 'has-error': errors.kana }"
            >
            <p v-if="errors.kana" class="error-message">{{ errors.kana }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">電話番号 <span class="required">※必須</span></label>
            <input 
              type="tel"
              class="form-control" 
              v-model="tel" 
              placeholder="例）09012341234"
              :class="{ 'has-error': errors.tel }"
            >
            <p v-if="errors.tel" class="error-message">{{ errors.tel }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">メールアドレス <span class="required">※必須</span></label>
            <input 
              type="email"
              class="form-control" 
              v-model="email" 
              placeholder="例）info@kaon-r.jp"
              :class="{ 'has-error': errors.email }"
            >
            <p v-if="errors.email" class="error-message">{{ errors.email }}</p>
          </div>

          <div class="form-group">
            <label class="form-label">お問い合わせ内容</label>
            <textarea 
              class="form-control" 
              v-model="comment"
              rows="6"
              placeholder="お問い合わせ内容をご記入ください"
              :class="{ 'has-error': errors.comment }"
            ></textarea>
            <p v-if="errors.comment" class="error-message">{{ errors.comment }}</p>
          </div>

          <div class="form-actions">
            <base-button
              v-if="!valid"
              :disabled="true"
              variant="primary"
              size="lg"
              block
            >
              未入力の必須項目があります
            </base-button>
            <base-button
              v-else-if="valid && !isLoading"
              variant="primary"
              size="lg"
              block
              type="button"
              @click="submit"
            >
              送信する
            </base-button>
            <base-button
              v-else-if="valid && isLoading"
              :disabled="true"
              variant="primary"
              size="lg"
              block
            >
              送信中...
            </base-button>
          </div>

          <transition name="fade">
            <div v-if="result" class="form-result">{{ result }}</div>
          </transition>
        </form>
      </div>
    </base-section>

    <!-- 連絡先情報セクション -->
    <base-section background="light">
      <div class="contact-info">
        <h3 class="contact-info__title">その他のお問い合わせ方法</h3>
        <div class="contact-info-grid">
          <div class="contact-info-item">
            <div class="contact-info-item__icon">
              <i class="fas fa-phone"></i>
            </div>
            <h4 class="contact-info-item__label">お電話でのお問い合わせ</h4>
            <p class="contact-info-item__value">03-6804-1059</p>
            <p class="contact-info-item__note">受付時間：平日 10:00-18:00</p>
          </div>
          <div class="contact-info-item">
            <div class="contact-info-item__icon">
              <i class="fas fa-envelope"></i>
            </div>
            <h4 class="contact-info-item__label">メールでのお問い合わせ</h4>
            <p class="contact-info-item__value">contact@kaon-r.jp</p>
            <p class="contact-info-item__note">24時間受付（返信は営業時間内）</p>
          </div>
          <div class="contact-info-item">
            <div class="contact-info-item__icon">
              <i class="fas fa-map-marker-alt"></i>
            </div>
            <h4 class="contact-info-item__label">所在地</h4>
            <p class="contact-info-item__value">東京都港区南青山2-2-15</p>
            <p class="contact-info-item__note">ウィン青山</p>
          </div>
        </div>
      </div>
    </base-section>
  </div>
</template>

<script>
import BaseSection from '@/components/common/BaseSection.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import { trackContactComplete } from '@/utils/analytics';

export default {
  name: 'ContactView',
  components: {
    BaseSection,
    BaseButton
  },
  data() {
    return {
      name: '',
      kana: '',
      tel: '',
      email: '',
      comment: '',
      result: '',
      errors: {
        name: '',
        kana: '',
        tel: '',
        email: '',
        comment: ''
      },
      isLoading: false
    };
  },
  computed: {
    valid() {
      return this.name && this.kana && this.tel && this.email;
    }
  },
  methods: {
    check() {
      this.errors = {
        name: '',
        kana: '',
        tel: '',
        email: '',
        comment: ''
      };
      
      let isValid = true;
      
      // 必須項目のチェック
      if (!this.name) {
        this.errors.name = 'お名前を入力してください';
        isValid = false;
      }
      
      if (!this.kana) {
        this.errors.kana = 'フリガナを入力してください';
        isValid = false;
      } else if (!/^[ァ-ヶー ]+$/.test(this.kana)) {
        this.errors.kana = 'フリガナはカタカナで入力してください';
        isValid = false;
      }
      
      if (!this.tel) {
        this.errors.tel = '電話番号を入力してください';
        isValid = false;
      } else if (!/^[0-9-]+$/.test(this.tel) || this.tel.length < 10) {
        this.errors.tel = '電話番号は正しい形式で入力してください';
        isValid = false;
      }
      
      if (!this.email) {
        this.errors.email = 'メールアドレスを入力してください';
        isValid = false;
      } else if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(this.email)) {
        this.errors.email = 'メールアドレスは正しい形式で入力してください';
        isValid = false;
      }
      
      return isValid;
    },
    submit() {
      // 既に送信中の場合は何もしない
      if (this.isLoading) {
        return;
      }
      
      if (this.check()) {
        this.isLoading = true;
        const params = {
          name: this.name,
          kana: this.kana,
          tel: this.tel,
          email: this.email,
          comment: this.comment
        };
        
        // Firebase Functions エンドポイント
        const endpoint = process.env.NODE_ENV === 'production' 
          ? 'https://asia-northeast1-kaonr-front.cloudfunctions.net/sendContactEmail'
          : 'http://localhost:5001/kaonr-front/asia-northeast1/sendContactEmail';
        
        this.axios.post(endpoint, params)
          .then(response => {
            console.log(response);
            this.result = '送信しました';
            this.isLoading = false;
            
            // フォームをリセット
            this.name = '';
            this.kana = '';
            this.tel = '';
            this.email = '';
            this.comment = '';
            
            // 自動返信メールの案内を表示
            alert('お問い合わせを受け付けました。\n自動返信メールをお送りしましたのでご確認ください。');
            
            // GA4コンバージョン追跡
            trackContactComplete({
              contact_type: 'general_inquiry'
            });
            
            // サンクスページへ遷移
            this.$router.push('/thanks');
          })
          .catch(error => {
            console.error('送信エラー:', error);
            this.result = '送信に失敗しました';
            this.isLoading = false;
            alert('送信に失敗しました。\n恐れ入りますが、時間をおいて再度お試しください。');
          });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.contact-view {
  padding-top: 0;
}

// ページヘッダー
.page-hero {
  background: linear-gradient(135deg, $primary-color 0%, $primary-light 100%);
  color: $white;
  padding: $spacing-4xl 0;
  text-align: center;
  
  @include media-breakpoint-down(md) {
    padding: $spacing-3xl 0;
  }
  
  &__title {
    font-size: $font-size-4xl;
    font-weight: 700;
    margin-bottom: $spacing-md;
    color: $white;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-3xl;
    }
  }
  
  &__subtitle {
    font-size: $font-size-xl;
    opacity: 0.9;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-lg;
    }
  }
}

// フォームラッパー
.contact-form-wrapper {
  max-width: 720px;
  margin: 0 auto;
}

// フォーム
.contact-form {
  background: $white;
  border-radius: $radius-lg;
  padding: $spacing-2xl;
  box-shadow: $shadow-sm;
  
  @include media-breakpoint-down(md) {
    padding: $spacing-xl;
  }
  
  @include media-breakpoint-down(sm) {
    padding: $spacing-lg;
  }
}

// フォームグループ
.form-group {
  margin-bottom: $spacing-xl;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-weight: 600;
  color: $gray-800;
  margin-bottom: $spacing-sm;
  font-size: $font-size-md;
  
  .required {
    color: $danger;
    font-size: $font-size-sm;
    margin-left: $spacing-xs;
  }
}

.form-control {
  width: 100%;
  padding: $spacing-sm $spacing-md;
  font-size: $font-size-md;
  line-height: 1.5;
  color: $gray-800;
  background-color: $white;
  border: 1px solid $gray-300;
  border-radius: $radius-md;
  transition: all $transition-base;
  
  &:focus {
    outline: none;
    border-color: $primary-color;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.1);
  }
  
  &.has-error {
    border-color: $danger;
  }
  
  &::placeholder {
    color: $gray-500;
  }
}

textarea.form-control {
  resize: vertical;
  min-height: 150px;
}

// エラーメッセージ
.error-message {
  color: $danger;
  font-size: $font-size-sm;
  margin-top: $spacing-xs;
  margin-bottom: 0;
}

// フォームアクション
.form-actions {
  margin-top: $spacing-2xl;
  text-align: center;
}

// 結果メッセージ
.form-result {
  margin-top: $spacing-lg;
  padding: $spacing-md;
  background-color: $success;
  color: $white;
  border-radius: $radius-md;
  text-align: center;
  font-weight: 600;
}

// 連絡先情報
.contact-info {
  text-align: center;
  
  &__title {
    font-size: $font-size-2xl;
    color: $primary-color;
    margin-bottom: $spacing-2xl;
    
    @include media-breakpoint-down(md) {
      font-size: $font-size-xl;
    }
  }
}

.contact-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $spacing-xl;
  
  @include media-breakpoint-down(md) {
    grid-template-columns: 1fr;
    gap: $spacing-lg;
  }
}

.contact-info-item {
  padding: $spacing-xl;
  
  &__icon {
    @include flex-center;
    width: 60px;
    height: 60px;
    margin: 0 auto $spacing-md;
    background-color: rgba($primary-color, 0.1);
    border-radius: $radius-full;
    color: $primary-color;
    font-size: $font-size-xl;
  }
  
  &__label {
    font-size: $font-size-md;
    font-weight: 600;
    color: $gray-800;
    margin-bottom: $spacing-xs;
  }
  
  &__value {
    font-size: $font-size-lg;
    color: $primary-color;
    font-weight: 600;
    margin-bottom: $spacing-xs;
  }
  
  &__note {
    font-size: $font-size-sm;
    color: $gray-600;
  }
}

// トランジション
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-base;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>